import React, { useEffect, useState } from 'react';
import { Gavel, Clock, Zap, ChevronRight, DollarSign, Loader2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { client } from '../api/client';
import { supabase } from '../lib/supabase';

function formatTimeRemaining(endTime: string): string {
  const end = new Date(endTime).getTime();
  const now = Date.now();
  const diff = (end - now) / 1000;

  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export const Marketplace = () => {
  const [auctions, setAuctions] = useState<any[]>([]);
  const [marketCards, setMarketCards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState<any>(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: auctionsData } = await supabase
          .from('auctions')
          .select('*, card:cards(*)')
          .eq('status', 'active');

        const { data: marketData } = await supabase
          .from('cards')
          .select('*')
          .eq('status', 'for_sale');

        setAuctions(auctionsData || []);

        // Transform auctions data to match UI expectations
        // The join returns card as an object, UI expects it.
        // Current bid is snake_case in DB (current_bid), UI uses camelCase (currentBid) in some places?
        // Let's normalize.
        const normalizedAuctions = (auctionsData || []).map((a: any) => ({
          ...a,
          currentBid: a.current_bid,
          endTime: a.end_time
        }));
        setAuctions(normalizedAuctions);

        setMarketCards(marketData || []);
      } catch (e) {
        console.error("Error fetching market data", e);
        setError("Failed to load marketplace data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleBuyNow = async (cardId: number) => {
    setProcessingId(cardId);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('market-ops', {
        body: { action: 'checkout', cardId }
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      } else {
        setError("Checkout failed: No payment URL returned.");
      }
    } catch (e: any) {
      console.error("Buy error", e);
      setError(`Checkout failed: ${e.message || 'Unknown error'}`);
    } finally {
      setProcessingId(null);
    }
  };

  const openBidModal = (auction: any) => {
    setSelectedAuction(auction);
    setBidAmount('');
    setBidModalOpen(true);
  };

  const handlePlaceBid = async () => {
    if (!selectedAuction || !bidAmount) return;
    setBidding(true);
    setError(null);
    try {
      const { error } = await supabase.functions.invoke('market-ops', {
        body: {
          action: 'bid',
          auctionId: selectedAuction.id,
          amount: parseFloat(bidAmount)
        }
      });

      if (error) throw error;

      setAuctions(prev => prev.map(a =>
        a.id === selectedAuction.id
          ? { ...a, currentBid: parseFloat(bidAmount) }
          : a
      ));
      setBidModalOpen(false);
    } catch (e: any) {
      console.error("Bid error", e);
      setError(e.message || 'Failed to place bid');
    } finally {
      setBidding(false);
    }
  };

  return (
    <div className="p-4 pt-14 space-y-8 pb-32 min-h-screen bg-obsidian">
      <header className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white font-display">Marketplace</h1>
        <div className="flex space-x-2">
          <span className="bg-danger/10 text-danger px-3 py-1 rounded-full text-xs font-bold flex items-center border border-danger/20">
            <div className="w-1.5 h-1.5 bg-danger rounded-full mr-2 animate-pulse" />
            LIVE
          </span>
        </div>
      </header>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-danger/90 text-cream-50 px-4 py-3 rounded-xl flex items-center justify-between"
          >
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="p-1 hover:bg-white/10 rounded-lg">
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-bold text-lg flex items-center text-white">
            <Gavel className="w-4 h-4 mr-2 text-copper-400" />
            Live Auctions
          </h2>
          <ChevronRight className="w-4 h-4 text-cream-200/40" />
        </div>

        {auctions.length === 0 ? (
          <div className="text-cream-200/40 text-sm italic">No active auctions</div>
        ) : (
          <div className="flex space-x-4 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
            {auctions.map((auction) => (
              <motion.div
                key={auction.id}
                whileTap={{ scale: 0.98 }}
                className="w-72 flex-shrink-0 glass rounded-2xl overflow-hidden relative group"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={auction.card?.image_url || 'https://placehold.co/300x200/1C1C1F/666?text=No+Image'}
                    alt={auction.card?.name || 'Auction card'}
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/300x200/1C1C1F/666?text=No+Image'; }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                  <div className="absolute bottom-3 left-3 bg-danger px-2 py-1 rounded-lg flex items-center text-xs font-bold text-white shadow-lg">
                    <Clock className="w-3 h-3 mr-1" />
                    {auction.endTime ? formatTimeRemaining(auction.endTime) : 'Live'}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-sm text-white mb-1">{auction.card?.name}</h3>
                  <p className="text-xs text-cream-200/40 mb-3">{auction.card?.set_name} {auction.card?.grade && `\u2022 ${auction.card.grade}`}</p>
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[10px] text-cream-200/40 uppercase tracking-wider">Current Bid</p>
                      <p className="font-bold text-xl text-white">
                        ${typeof auction.currentBid === 'number' ? auction.currentBid.toFixed(2) : auction.currentBid}
                      </p>
                    </div>
                    <button
                      onClick={() => openBidModal(auction)}
                      className="bg-copper-500 hover:bg-copper-400 text-cream-50 px-4 py-2 rounded-xl text-xs font-bold transition-colors"
                    >
                      Bid Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-bold text-lg mb-4 flex items-center text-white">
          <Zap className="w-4 h-4 mr-2 text-warning" />
          Buy Now
        </h2>
        <div className="space-y-3">
          {marketCards.length === 0 ? (
            <div className="text-cream-200/40 text-sm italic">No cards for sale</div>
          ) : (
            marketCards.map((card) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="glass p-3 rounded-2xl flex space-x-4 group cursor-pointer hover:bg-white/5 transition-colors"
              >
                <div className="w-24 h-24 rounded-xl overflow-hidden relative flex-shrink-0">
                  <img
                    src={card.image_url || card.imageUrl || 'https://placehold.co/100x100/1C1C1F/666?text=No+Image'}
                    alt={card.name || 'Card for sale'}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/100x100/1C1C1F/666?text=No+Image'; }}
                  />
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <h3 className="font-bold text-white mb-1">{card.name}</h3>
                  <p className="text-xs text-cream-200/40 mb-2">{card.setName || card.set_name} {card.grade && `\u2022 ${card.grade}`}</p>
                  <div className="flex justify-between items-center mt-auto">
                    <span className="font-bold text-lg text-copper-400">
                      ${typeof card.price === 'number' ? card.price.toFixed(2) : card.price}
                    </span>
                    <button
                      onClick={() => handleBuyNow(card.id)}
                      disabled={processingId === card.id}
                      className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center disabled:opacity-50"
                    >
                      {processingId === card.id ? (
                        <Loader2 size={12} className="animate-spin mr-1" />
                      ) : (
                        <DollarSign size={12} className="mr-1" />
                      )}
                      Buy
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </section>

      <AnimatePresence>
        {bidModalOpen && selectedAuction && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-carbon border border-white/[0.06] rounded-2xl p-6 w-full max-w-sm shadow-elevated"
            >
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-display font-semibold text-cream-50">Place Bid</h3>
                <button onClick={() => setBidModalOpen(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-cream-200/50 hover:text-cream-100 transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="mb-4">
                <p className="text-sm text-cream-200/60">{selectedAuction.card?.name}</p>
                <p className="text-xs text-cream-200/40">{selectedAuction.card?.set_name}</p>
              </div>
              <div className="mb-4 p-3 card-premium">
                <p className="text-[10px] text-cream-200/40 uppercase tracking-wide mb-1">Current Bid</p>
                <p className="text-2xl font-display font-semibold text-cream-50">
                  ${typeof selectedAuction.currentBid === 'number' ? selectedAuction.currentBid.toFixed(2) : selectedAuction.currentBid}
                </p>
              </div>
              <div className="mb-5">
                <label className="text-xs text-cream-200/50 mb-1.5 block font-medium">Your Bid ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream-200/40" />
                  <input
                    type="number"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    placeholder={`Min: $${((selectedAuction.currentBid || 0) + 0.01).toFixed(2)}`}
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-10 pr-4 text-cream-100 placeholder-cream-200/30 focus:outline-none focus:border-copper-400/50 transition-colors"
                  />
                </div>
              </div>
              <button
                onClick={handlePlaceBid}
                disabled={bidding || !bidAmount || parseFloat(bidAmount) <= (selectedAuction.currentBid || 0)}
                className="w-full py-3.5 bg-copper-500 text-cream-50 rounded-xl font-semibold hover:bg-copper-400 transition-colors disabled:opacity-50"
              >
                {bidding ? 'Placing Bid...' : 'Place Bid'}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
